import Skeleton from "@/components/Skeleton/Skeleton";
import { VisualizationProps } from "@/components/visualizations/Visualization";
import NoDataPoints from "../NoDataPoints/NoDataPoints";
import useVisualization from "../useVisualization";
import styles from "./TreeMapChart.module.scss";

import { Chart as ChartJS } from "chart.js";

import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import { Chart } from "react-chartjs-2";

ChartJS.register(TreemapController, TreemapElement);

export type TreeMapChartData = { category: string; value: number; onClick?: () => void }[];

export interface TreeMapChartProps extends VisualizationProps {
  data: TreeMapChartData;
  labelToBackgroundColor?: (label?: string) => string;
  labelToFontColor?: (label?: string) => string;
}

export default function TreeMapChart(props: TreeMapChartProps) {
  const { data, loading } = props;
  const { formatLabel } = useVisualization(props);

  const chartData = {
    datasets: [
      {
        tree: data,
        key: "value",
        groups: ["category"],
        spacing: 5,
        borderRadius: 8,
        labels: {
          display: true,
          position: "top",
          align: "left",
          padding: 12,
          formatter: (context: any) => [
            context.raw.g,
            props.format?.(context.raw.v) || context.raw.v,
          ],
          color: (ctx: any) => {
            return props.labelToFontColor?.(ctx?.raw?._data?.children[0]?.mainCategory);
          },
          font: {
            family:
              "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif",
            size: "12px",
          },
        },
        backgroundColor: (ctx: any) => {
          return props.labelToBackgroundColor?.(ctx?.raw?._data?.children[0]?.mainCategory);
        },
      },
    ],
  };

  const options = {
    animations: false,
    maintainAspectRatio: false,
    elements: {
      treemap: {
        captions: {
          display: true,
        },
      },
    },
    onClick: (_: any, action: any) => {
      // look up by label, and perform the action (yes, this is horrible!)
      const label = action[0]?.element?.["$context"]?.raw?.g;
      const item = data.find((x) => x.category === label);
      if (item) item.onClick?.();
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (context: any) => context[0]?.raw?.g,
          label: (context: any) => formatLabel(context.raw.v),
        },
      },
      datalabels: {
        display: false,
        textStrokeColor: "#fff",
        textStrokeWidth: 3,
        font: {
          weight: 600,
        },
        color: "#000",
        formatter: (value: any) => {
          return props.format?.(value) || value;
        },
      },
    },
  };

  const sum = data.reduce((a, b) => a + b.value, 0);

  return (
    <div className={styles.TreeMapChart}>
      {!loading && (data.length === 0 || sum === 0) ? (
        <NoDataPoints />
      ) : (
        <>
          <div className={styles.chart}>
            {loading ? (
              <div style={{ padding: 5, width: "100%", height: "100%", display: "flex", gap: 10 }}>
                <Skeleton
                  style={{
                    flexGrow: 1,
                    width: "20%",
                    height: "100%",
                  }}
                />
                <Skeleton
                  style={{
                    flexGrow: 1,
                    width: "10%",
                    height: "100%",
                  }}
                />
                <div
                  style={{
                    width: "40%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <Skeleton
                    style={{
                      flexGrow: 1,
                      width: "100%",
                      height: "30%",
                    }}
                  />
                  <Skeleton
                    style={{
                      flexGrow: 1,
                      width: "100%",
                      height: "40%",
                    }}
                  />
                </div>
              </div>
            ) : (
              <Chart
                type="treemap"
                data={chartData as any}
                options={options as any}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
